import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CustomCommandForm from './CustomCommandForm';
import CustomCommandsSkeleton from './CustomCommandsSkeleton';
import {
  CustomCommand,
  CreateCustomCommandInput,
  getCustomCommands,
  createCustomCommand,
  updateCustomCommand,
  deleteCustomCommand,
  toggleCustomCommand,
  processTemplateVariables
} from '../lib/customCommands';
import { loadRumbleSettings } from '../lib/rumbleApi';
import { isUserWhitelisted } from '../lib/whitelist';

interface CustomCommandsProps {
  onLogin?: () => void;
  onNavigateToSettings?: () => void;
}

const CustomCommands: React.FC<CustomCommandsProps> = ({ onLogin, onNavigateToSettings }) => {
  const [commands, setCommands] = useState<CustomCommand[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCommand, setEditingCommand] = useState<CustomCommand | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState<string | React.ReactNode | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; commandId: string | null }>({
    isOpen: false,
    commandId: null
  });
  const [hasRumbleApi, setHasRumbleApi] = useState(false);
  const [isWhitelisted, setIsWhitelisted] = useState(false);

  const { user } = useAuth();

  // Check if user has Rumble API connected
  useEffect(() => {
    const checkRumbleApi = async () => {
      if (user) {
        try {
          const settings = await loadRumbleSettings();
          setHasRumbleApi(settings?.api_key && settings?.rumble_username ? true : false);
        } catch (err) {
          setHasRumbleApi(false);
        }
      }
    };
    checkRumbleApi();
  }, [user]);

  // Check if user is whitelisted for custom commands
  useEffect(() => {
    const checkWhitelist = async () => {
      if (user) {
        try {
          const whitelisted = await isUserWhitelisted('custom-commands');
          setIsWhitelisted(whitelisted);
        } catch (err) {
          setIsWhitelisted(false);
        }
      }
    };
    checkWhitelist();
  }, [user]);

  // Load commands on component mount
  useEffect(() => {
    if (user) {
      loadCommands();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadCommands = async () => {
    try {
      setLoading(true);
      const data = await getCustomCommands();
      setCommands(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load commands');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCommand = async (input: CreateCustomCommandInput) => {
    try {
      setFormLoading(true);
      await createCustomCommand(input);
      await loadCommands();
      setShowForm(false);
      setError(null);
    } catch (err) {
      // Check for specific error types
      if (err instanceof Error && err.message === 'NO_RUMBLE_API') {
        setError(
          <>
            You need to connect your Rumble Live Stream API in the{' '}
            <button
              onClick={() => onNavigateToSettings?.()}
              className="text-blue-400 hover:text-blue-300 underline"
            >
              Settings
            </button>
            {' '}tab before creating custom commands.
          </>
        );
        setFormLoading(false);
        return; // Don't throw, we handled it
      } else if (err instanceof Error && err.message === 'NOT_WHITELISTED') {
        setError(
          <>
            You need to be whitelisted to create custom commands. Please reach out to{' '}
            <a
              href="https://x.com/whizkick"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline"
            >
              @whizkick on X
            </a>
            {' '}for access.
          </>
        );
        setFormLoading(false);
        return; // Don't throw, we handled it
      }
      throw err; // Let form handle other errors
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateCommand = async (input: CreateCustomCommandInput) => {
    if (!editingCommand) return;
    
    try {
      setFormLoading(true);
      await updateCustomCommand(editingCommand.id!, input);
      await loadCommands();
      setEditingCommand(null);
      setError(null);
    } catch (err) {
      throw err; // Let form handle the error
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteCommand = async (id: string) => {
    try {
      await deleteCustomCommand(id);
      await loadCommands();
      setDeleteConfirm({ isOpen: false, commandId: null });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete command');
    }
  };

  const handleToggleCommand = async (id: string, enabled: boolean) => {
    try {
      await toggleCustomCommand(id, enabled);
      await loadCommands();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle command');
    }
  };

  const confirmDelete = (id: string) => {
    setDeleteConfirm({ isOpen: true, commandId: id });
  };

  const formatTrigger = (command: CustomCommand) => {
    const prefix = command.requires_prefix ? '!' : '';
    return prefix + (command.trigger_command || command.name || '');
  };

  const getPreviewResponse = (command: CustomCommand) => {
    return processTemplateVariables(command.response_message || command.response || '', {
      senderUsername: '@viewer',
      commandTrigger: formatTrigger(command),
      userCount: 42,
      uptime: '2h 15m'
    });
  };

  // Show skeleton while loading
  if (loading) {
    return <CustomCommandsSkeleton />;
  }

  if (!user) {
    return (
      <section id="custom-commands" className="content-section flex items-center justify-center min-h-[500px]">
        <div className="max-w-md w-full text-center p-8 border-2 border-white bg-black">
          <div className="mb-6">
            <i className="fa-solid fa-lock text-6xl text-white mb-4 inline-block"></i>
          </div>
          <h2 className="text-3xl font-bold text-white mb-3">Authentication Required</h2>
          <p className="text-gray-300 mb-6 text-base leading-relaxed">
            Sign in to create and manage your custom commands. We use magic link authentication - no passwords needed!
          </p>

          <div className="space-y-4">
            <button
              onClick={onLogin}
              className="paint-button w-full text-lg py-3"
            >
              <i className="fa-solid fa-envelope mr-2"></i>
              Sign In with Magic Link
            </button>

            <div className="flex items-center gap-2 text-xs text-gray-400 justify-center">
              <i className="fa-solid fa-info-circle"></i>
              <span>We'll send a secure link to your email</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="custom-commands" className="content-section">
      {/* Closed Beta Alert */}
      {!hasRumbleApi ? (
        <div className="mb-6 p-4 border-2 border-yellow-500 bg-yellow-900 bg-opacity-20 text-yellow-200">
          <div className="flex items-start gap-3">
            <i className="fa-solid fa-info-circle text-yellow-400 mt-1"></i>
            <div>
              <p className="font-semibold mb-1">Setup Required</p>
              <p className="text-sm mb-2">
                Before creating custom commands, you need to connect your Rumble Live Stream API in{' '}
                <button
                  onClick={() => onNavigateToSettings?.()}
                  className="text-blue-400 hover:text-blue-300 underline font-medium"
                >
                  Settings
                </button>
                .
              </p>
              <p className="text-sm">
                You'll also need to be whitelisted for closed beta access. Please reach out to{' '}
                <a
                  href="https://x.com/whizkick"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline font-medium"
                >
                  @whizkick on X
                </a>
                {' '}for whitelisting.
              </p>
            </div>
          </div>
        </div>
      ) : !isWhitelisted ? (
        <div className="mb-6 p-4 border-2 border-blue-500 bg-blue-900 bg-opacity-20 text-blue-200">
          <div className="flex items-start gap-3">
            <i className="fa-solid fa-info-circle text-blue-400 mt-1"></i>
            <div>
              <p className="font-semibold mb-1">Closed Beta Access</p>
              <p className="text-sm">
                Custom commands require whitelist access. Please reach out to{' '}
                <a
                  href="https://x.com/whizkick"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-300 hover:text-blue-200 underline font-medium"
                >
                  @whizkick on X
                </a>
                {' '}for whitelisting.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex justify-between items-center mb-6 header-border pb-4">
        <h2 className="text-xl font-semibold text-white">custom commands</h2>
        <button
          className="paint-button paint-button-sm"
          onClick={() => {
            if (!hasRumbleApi) {
              setError(
                <>
                  You need to connect your Rumble Live Stream API in the{' '}
                  <button
                    onClick={() => onNavigateToSettings?.()}
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    Settings
                  </button>
                  {' '}tab before creating custom commands.
                </>
              );
            } else if (!isWhitelisted) {
              setError(
                <>
                  You need to be whitelisted to create custom commands. Please reach out to{' '}
                  <a
                    href="https://x.com/whizkick"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    @whizkick on X
                  </a>
                  {' '}for access.
                </>
              );
            } else {
              setShowForm(true);
            }
          }}
        >
          <i className="fa-solid fa-plus mr-1"></i>
          add command
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 border-2 border-red-500 text-red-400">
          <i className="fa-solid fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {commands.length === 0 ? (
        <div className="text-center p-8">
          <i className="fa-solid fa-terminal text-4xl text-gray-400 mb-4"></i>
          <h3 className="text-lg font-semibold text-white mb-2">No Custom Commands</h3>
          <p className="text-gray-400 mb-4">Create your first custom command to get started</p>
          <button
            className="paint-button"
            onClick={() => {
              if (!hasRumbleApi) {
                setError(
                  <>
                    You need to connect your Rumble Live Stream API in the{' '}
                    <button
                      onClick={() => onNavigateToSettings?.()}
                      className="text-blue-400 hover:text-blue-300 underline"
                    >
                      Settings
                    </button>
                    {' '}tab before creating custom commands.
                  </>
                );
              } else if (!isWhitelisted) {
                setError(
                  <>
                    You need to be whitelisted to create custom commands. Please reach out to{' '}
                    <a
                      href="https://x.com/whizkick"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 underline"
                    >
                      @whizkick on X
                    </a>
                    {' '}for access.
                  </>
                );
              } else {
                setShowForm(true);
              }
            }}
          >
            <i className="fa-solid fa-plus mr-2"></i>
            Add Your First Command
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {commands.map((command) => (
            <div 
              key={command.id} 
              className={`paint-card p-4 ${!command.enabled ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <button
                      onClick={() => handleToggleCommand(command.id!, !command.enabled)}
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                        command.enabled 
                          ? 'bg-green-500 border-green-500 text-white' 
                          : 'border-gray-400 hover:border-white'
                      }`}
                      title={command.enabled ? 'Disable command' : 'Enable command'}
                    >
                      {command.enabled && <i className="fa-solid fa-check text-xs"></i>}
                    </button>
                    
                    <code className={`font-mono font-bold px-2 py-1 rounded ${
                      command.enabled ? 'text-blue-400 bg-blue-900 bg-opacity-30' : 'text-gray-400 bg-gray-700'
                    }`}>
                      {formatTrigger(command)}
                    </code>
                    
                    {!command.case_sensitive && (
                      <span className="text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
                        case insensitive
                      </span>
                    )}
                  </div>
                  
                  <div className="mb-2">
                    <p className={`text-sm mb-1 ${command.enabled ? 'text-gray-300' : 'text-gray-500'}`}>
                      <strong>Response:</strong> "{command.response_message || command.response || ''}"
                    </p>
                    <p className="text-xs text-gray-400">
                      <strong>Preview:</strong> "{getPreviewResponse(command)}"
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span>Used {command.usage_count || 0} times</span>
                    {command.last_used_at && (
                      <span>
                        Last used: {new Date(command.last_used_at).toLocaleDateString()}
                        {command.last_used_by && ` by @${command.last_used_by}`}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => setEditingCommand(command)}
                    className="text-gray-400 hover:text-white transition-colors"
                    title="Edit command"
                  >
                    <i className="fa-solid fa-edit"></i>
                  </button>
                  <button
                    onClick={() => confirmDelete(command.id!)}
                    className="text-gray-400 hover:text-red-400 transition-colors"
                    title="Delete command"
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Command Form */}
      {showForm && (
        <CustomCommandForm
          onSubmit={handleCreateCommand}
          onCancel={() => setShowForm(false)}
          loading={formLoading}
        />
      )}

      {/* Edit Command Form */}
      {editingCommand && (
        <CustomCommandForm
          onSubmit={handleUpdateCommand}
          onCancel={() => setEditingCommand(null)}
          loading={formLoading}
          initialData={{
            trigger_command: editingCommand.name || editingCommand.trigger_command,
            response_message: editingCommand.response || editingCommand.response_message,
            enabled: editingCommand.enabled,
            case_sensitive: editingCommand.case_sensitive,
            requires_prefix: true // Always required
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-black border-2 border-white p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">Delete Command</h3>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete this custom command? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm({ isOpen: false, commandId: null })}
                className="paint-button paint-button-sm bg-gray-700 hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteConfirm.commandId && handleDeleteCommand(deleteConfirm.commandId)}
                className="paint-button paint-button-sm bg-red-600 hover:bg-red-700"
              >
                <i className="fa-solid fa-trash mr-2"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CustomCommands;